import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Clock, Loader2, Lock, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parseKey = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const prettyDate = (k) =>
  parseKey(k).toLocaleDateString("nl-BE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

const inputCls =
  "w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent text-ink";

const EventBooking = ({ event }) => {
  const [dates, setDates] = useState({ available: [], full: [] });
  const [month, setMonth] = useState(new Date());
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    persons: "",
    notes: "",
  });

  const price = event.sale_price || event.price;

  const loadDates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${event.slug}/dates`);
      setDates(data);
      if (data.available?.length) setMonth(parseKey(data.available[0]));
    } catch {
      /* ignore */
    }
  }, [event.slug]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  const pickDate = async (day) => {
    if (!day) return;
    const key = toKey(day);
    setDate(key);
    setSlotId(null);
    setLoadingSlots(true);
    try {
      const { data } = await axios.get(`${API}/events/${event.slug}/slots`, {
        params: { date: key },
      });
      setSlots(data);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    if (!slotId) {
      toast.error("Kies eerst een tijdslot.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/events/${event.slug}/checkout`, {
        slot_id: slotId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        persons: form.persons ? Number(form.persons) : null,
        notes: form.notes || null,
        terms_accepted: true,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Er ging iets mis. Probeer het opnieuw.");
      setBusy(false);
      await pickDate(parseKey(date));
      loadDates();
    }
  };

  const availableDays = (dates.available || []).map(parseKey);
  const hasDays = availableDays.length > 0 || (dates.full || []).length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      {/* Left: info */}
      <div className="lg:col-span-5">
        {event.image_url && (
          <div className="overflow-hidden mb-8">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-72 object-cover transition-transform duration-700 hover:scale-[1.04]"
            />
          </div>
        )}
        <p className="eyebrow mb-3">Tijdelijke actie</p>
        <h2 className="font-serif text-4xl md:text-5xl leading-tight">{event.title}</h2>
        {event.subtitle && (
          <p className="mt-4 text-base md:text-lg text-neutral-600">{event.subtitle}</p>
        )}

        <div className="flex items-end gap-4 mt-8">
          <span className="font-serif text-5xl text-gold" data-testid={`event-price-${event.slug}`}>
            €{Number(price).toFixed(0)}
          </span>
          {event.sale_price && (
            <span className="text-neutral-400 line-through text-lg mb-2">
              €{Number(event.price).toFixed(0)}
            </span>
          )}
        </div>

        {event.description && (
          <p className="mt-7 text-ink/70 leading-relaxed">{event.description}</p>
        )}

        {event.includes?.length > 0 && (
          <ul className="mt-8 space-y-3">
            {event.includes.map((inc) => (
              <li key={inc} className="flex items-start gap-3 text-[15px] text-ink/75">
                <Check size={15} className="text-gold mt-1 shrink-0" />
                <span>{inc}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 space-y-2 text-sm text-neutral-500">
          <p className="flex items-center gap-2">
            <Clock size={14} className="text-gold" />
            {event.shoot_minutes} minuten fotograferen — {event.slot_minutes} minuten per boeking
          </p>
          <p className="flex items-center gap-2">
            <CalendarDays size={14} className="text-gold" />
            {event.location}
          </p>
          {event.extra_photo_price ? (
            <p className="flex items-center gap-2">
              <Check size={14} className="text-gold" />
              Extra foto's: €{Number(event.extra_photo_price).toFixed(0)} per stuk
            </p>
          ) : null}
        </div>
      </div>

      {/* Right: booking */}
      <div className="lg:col-span-7">
        {!hasDays ? (
          <div className="bg-beige border-l-2 border-gold p-10 text-center">
            <CalendarDays size={24} className="text-gold mx-auto" strokeWidth={1.3} />
            <p className="font-serif text-2xl mt-5">Data komen binnenkort online</p>
            <p className="text-sm text-neutral-600 mt-3">
              De sessies voor deze actie zijn nog niet opengezet. Schrijf je in op de nieuwsbrief en
              je weet het als eerste.
            </p>
          </div>
        ) : (
          <form onSubmit={pay} data-testid={`event-form-${event.slug}`} className="space-y-8">
            <div className="bg-beige p-6 md:p-8 border-l-2 border-gold">
              <p className="eyebrow mb-5 text-neutral-500">1. Kies je dag</p>
              <div className="bg-white p-2 sm:p-4 inline-block" data-testid={`event-calendar-${event.slug}`}>
                <Calendar
                  mode="single"
                  selected={date ? parseKey(date) : undefined}
                  onSelect={pickDate}
                  month={month}
                  onMonthChange={setMonth}
                  fromDate={new Date()}
                  disabled={(day) => !(dates.available || []).includes(toKey(day))}
                  modifiers={{ openDay: availableDays }}
                  modifiersClassNames={{
                    openDay: "font-semibold text-ink",
                  }}
                  classNames={{
                    day_selected:
                      "bg-gold text-white hover:bg-gold hover:text-white focus:bg-gold focus:text-white",
                    day_disabled: "text-neutral-300 opacity-100 hover:bg-transparent cursor-not-allowed",
                  }}
                />
              </div>
              <p className="text-xs text-neutral-500 mt-4">
                Enkel dagen met vrije tijdsloten zijn aanklikbaar.
              </p>
            </div>

            {date && (
              <div>
                <p className="eyebrow mb-5 text-neutral-500">
                  2. Kies je uur — {prettyDate(date)}
                </p>
                {loadingSlots ? (
                  <p className="inline-flex items-center gap-2 text-sm text-neutral-500">
                    <Loader2 size={14} className="animate-spin" /> Tijdsloten laden...
                  </p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-neutral-500">Geen tijdsloten meer op deze dag.</p>
                ) : (
                  <div
                    className="grid grid-cols-3 sm:grid-cols-4 gap-3"
                    data-testid={`event-slots-${event.slug}`}
                  >
                    {slots.map((s) => {
                      const free = s.status === "open";
                      const active = slotId === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`event-slot-${s.time}`}
                          disabled={!free}
                          onClick={() => setSlotId(s.id)}
                          className={`py-3 text-sm tracking-wide transition-colors border ${
                            active
                              ? "bg-gold text-white border-gold"
                              : free
                              ? "bg-white border-neutral-200 hover:border-gold hover:text-gold"
                              : "bg-neutral-100 text-neutral-400 border-neutral-100 line-through cursor-not-allowed"
                          }`}
                        >
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {slotId && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                <p className="eyebrow text-neutral-500">3. Jouw gegevens</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">Naam</span>
                    <input
                      data-testid="event-name"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={update("name")}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">E-mail</span>
                    <input
                      data-testid="event-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">Telefoon</span>
                    <input
                      data-testid="event-phone"
                      required
                      minLength={6}
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">
                      Aantal personen (optioneel)
                    </span>
                    <input
                      data-testid="event-persons"
                      type="number"
                      min={1}
                      max={20}
                      value={form.persons}
                      onChange={update("persons")}
                      className={inputCls}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="eyebrow block mb-2 text-neutral-500">
                    Extra wensen (optioneel)
                  </span>
                  <textarea
                    data-testid="event-notes"
                    rows={3}
                    maxLength={1000}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="Met onze hond, kerstoutfit, ..."
                    className={`${inputCls} resize-none`}
                  />
                </label>

                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    data-testid="event-terms"
                    type="checkbox"
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-gold shrink-0"
                  />
                  <span className="text-sm text-neutral-700 leading-relaxed">
                    Ik heb de{" "}
                    <Link to="/voorwaarden" target="_blank" className="text-gold hover:underline">
                      voorwaarden
                    </Link>{" "}
                    gelezen en ga ermee akkoord. Je tijdslot wordt 15 minuten vrijgehouden om af te
                    rekenen.
                  </span>
                </label>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t border-neutral-200">
                  <p className="flex items-center gap-2 text-sm text-neutral-600">
                    <Lock size={14} className="text-gold" />
                    Beveiligd betalen via Stripe
                  </p>
                  <button
                    data-testid={`event-pay-${event.slug}`}
                    type="submit"
                    disabled={busy || !terms}
                    className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busy ? "Bezig..." : `Boek & betaal €${Number(price).toFixed(0)}`}
                  </button>
                </div>
              </motion.div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default EventBooking;
